// src/app/AppShell.js
import React, { Suspense, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Outlet, Link as RouterLink, useBlocker, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Container,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Paper,
  Stack,
  useMediaQuery,
  Snackbar,
  Drawer,
  Divider,
  useTheme,
} from "@mui/material";

import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import NearMeRoundedIcon from "@mui/icons-material/NearMeRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import WbSunnyRoundedIcon from "@mui/icons-material/WbSunnyRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import AddLocationAltRoundedIcon from "@mui/icons-material/AddLocationAltRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import LocalActivityRoundedIcon from "@mui/icons-material/LocalActivityRounded";
import LandscapeRoundedIcon from "@mui/icons-material/LandscapeRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import { keyframes } from "@emotion/react";

import { MoodContext } from "./MoodContext";
import { MOODS } from "../theme/moods";
import { useAuth } from "../context/auth";
import { useDailyMood } from "../features/mood/DailyMoodContext";
import { useMileBalance } from "../features/miles/hooks/useMileBalance";
import DailyMoodScreen from "../features/mood/DailyMoodScreen";
import { MOOD_GRADIENTS } from "../features/mood/dailyMoods";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../api/firebase";
import { LOGIN_OPEN } from "../config/loginGate";

const BOTTOM_NAV_HEIGHT = 64;
// 「今日の気分は？」（初回フルスクリーン＋ヘッダーの気分チップ）。2026-10-13 一旦停止。
// /today ページ自体は残っている。復活させるときはここを true にするだけ
const DAILY_MOOD_ENABLED = false;
// 「ログイン」「無料で始める」の入口ボタン。config/loginGate の LOGIN_OPEN に従う
const ENTRY_BUTTONS_ENABLED = LOGIN_OPEN;

/* ─── Drawerカードアニメーション（常時ループ、ホバーで一時停止） ─── */
const kSunSpin    = keyframes({ "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } });
const kBounce     = keyframes({ "0%, 100%": { transform: "translateY(0)" }, "50%": { transform: "translateY(-7px)" } });
const kTwinkle    = keyframes({ "0%, 100%": { transform: "scale(1)", opacity: 1 }, "50%": { transform: "scale(1.4) rotate(15deg)", opacity: 0.7 } });
const kWiggle     = keyframes({ "0%, 100%": { transform: "rotate(0deg)" }, "25%": { transform: "rotate(-12deg)" }, "75%": { transform: "rotate(12deg)" } });
const kFloat      = keyframes({ "0%, 100%": { transform: "translateY(0) scale(1)" }, "50%": { transform: "translateY(-5px) scale(1.08)" } });
const kPulse      = keyframes({ "0%, 100%": { transform: "scale(1)" }, "50%": { transform: "scale(1.22)" } });
const kCogSpin    = keyframes({ "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } });
const kSlideRight = keyframes({ "0%, 100%": { transform: "translateX(0)" }, "50%": { transform: "translateX(5px)" } });

const DRAWER_ITEM_DEFS = {
  "/today":            { Icon: WbSunnyRoundedIcon,           color: "#f59e0b", anim: kSunSpin,    loop: "7s linear" },
  "/astro":            { Icon: AutoAwesomeRoundedIcon,        color: "#8b5cf6", anim: kTwinkle,    loop: "2.2s ease-in-out" },
  "/shrines/register": { Icon: AddLocationAltRoundedIcon,     color: "#f97316", anim: kFloat,      loop: "2.5s ease-in-out" },
  "/admin":            { Icon: AdminPanelSettingsRoundedIcon, color: "#64748b", anim: kCogSpin,    loop: "5s linear" },
  "/home":             { Icon: HomeRoundedIcon,               color: "#6366f1", anim: kBounce,     loop: "2s ease-in-out" },
  "/nearby":           { Icon: NearMeRoundedIcon,             color: "#06b6d4", anim: kPulse,      loop: "2s ease-in-out" },
  "/keidai":           { Icon: LandscapeRoundedIcon,          color: "#059669", anim: kFloat,      loop: "2.8s ease-in-out" },
  "/me/history":       { Icon: HistoryRoundedIcon,            color: "#0891b2", anim: kSlideRight, loop: "2s ease-in-out" },
  "/me/blog":          { Icon: MenuBookRoundedIcon,           color: "#92400e", anim: kWiggle,     loop: "3s ease-in-out" },
  "/me":               { Icon: PersonRoundedIcon,             color: "#6366f1", anim: kFloat,      loop: "3s ease-in-out" },
  "/start":            { Icon: PersonAddRoundedIcon,          color: "#10b981", anim: kBounce,     loop: "1.8s ease-in-out" },
  "logout":            { Icon: LogoutRoundedIcon,             color: "#ef4444", anim: kSlideRight, loop: "2.5s ease-in-out" },
};

function DrawerCard({ to, defKey, label, isActive, onClick }) {
  const def = DRAWER_ITEM_DEFS[defKey || to];
  const active = to ? isActive?.(to) : false;
  if (!def) return null;
  const { Icon, color, anim, loop } = def;

  const cardSx = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.7,
    py: 1.75,
    px: 1,
    borderRadius: "16px",
    border: "1.5px solid",
    borderColor: active ? color + "66" : color + "22",
    bgcolor: active ? color + "22" : color + "0e",
    cursor: "pointer",
    textDecoration: "none",
    transition: "transform 0.16s ease, box-shadow 0.16s ease, background-color 0.16s ease",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
    "& .dc-icon": {
      animation: `${anim} ${loop} infinite`,
    },
    "&:hover": {
      bgcolor: color + "28",
      borderColor: color + "66",
      transform: "scale(1.05)",
      boxShadow: `0 6px 20px ${color}30`,
      "& .dc-icon": { animationPlayState: "paused", transform: "scale(1.12)" },
    },
    "&:active": { transform: "scale(0.96)" },
  };

  const inner = (
    <>
      <Icon className="dc-icon" sx={{ color: active ? color : color + "bb", fontSize: "1.75rem" }} />
      <Typography sx={{ fontWeight: active ? 900 : 700, fontSize: "0.66rem", color: active ? color : "text.primary", letterSpacing: 0.3, textAlign: "center", lineHeight: 1.3 }}>
        {label}
      </Typography>
    </>
  );

  if (onClick) return <Box onClick={onClick} sx={cardSx}>{inner}</Box>;
  return <Box component={RouterLink} to={to} sx={cardSx}>{inner}</Box>;
}

function DrawerCardGrid({ children }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, px: 2, py: 1 }}>
      {children}
    </Box>
  );
}

/* ─── BottomNav 定義 ─── */
const bottomNavItems = [
  { label: "ホーム", to: "/home", icon: <HomeRoundedIcon /> },
  { label: "巡る", to: "/nearby", icon: <NearMeRoundedIcon /> },
  { label: "社", to: "/keidai", icon: <LandscapeRoundedIcon /> },
  { label: "ログ", to: "/me/history", icon: <HistoryRoundedIcon /> },
];

/* ─── フッター（PCのみ表示） ─── */
function Footer({ dark = false, guest = false }) {
  const theme = useTheme();
  const links = [
    { label: "ホーム", to: "/" },
    // 神社を探す・登録はログイン後の行為。未ログインには出さない
    // （「神社を探す」→参拝画面→ログイン、の導線を断つ。2026-11-03。ボトムナビの「巡る」と揃える）
    ...(guest ? [] : [{ label: "神社を探す", to: "/nearby" }, { label: "神社を登録", to: "/shrines/register" }]),
    { label: "利用規約", to: "/terms" },
    { label: "プライバシー", to: "/privacy" },
    { label: "クレジット", to: "/credits" },
  ];
  return (
    <Box
      component="footer"
      sx={{
        mt: "auto",
        pt: 6,
        pb: 4,
        px: 2,
        textAlign: "center",
        color: dark ? "rgba(222,215,198,0.5)" : "text.secondary",
        borderTop: `1px solid ${dark ? "rgba(212,180,110,0.14)" : theme.palette.divider}`,
      }}
    >
      <Stack
        direction="row"
        justifyContent="center"
        spacing={3}
        sx={{ mb: 2, flexWrap: "wrap" }}
      >
        {links.map((item) => (
          <Typography
            key={item.to}
            component={RouterLink}
            to={item.to}
            variant="body2"
            sx={{
              color: dark ? "rgba(222,215,198,0.55)" : "text.secondary",
              textDecoration: "none",
              fontWeight: dark ? 500 : 600,
              ...(dark && {
                fontFamily: '"Hiragino Mincho ProN", "Yu Mincho", serif',
                fontSize: "0.78rem",
                letterSpacing: 2,
              }),
              "&:hover": { color: dark ? "#ece5d4" : "text.primary" },
              transition: "color 0.2s",
            }}
          >
            {item.label}
          </Typography>
        ))}
      </Stack>

      <Typography variant="caption" sx={{ opacity: 0.6 }}>
        &copy; {new Date().getFullYear()} 株式会社神和導
      </Typography>
    </Box>
  );
}

/* ─── メイン ─── */
export default function AppShell() {
  const { user: authUser, userDoc, signOut, isAdmin, patchUserDoc } = useAuth();
  const plan = userDoc?.plan || "guest";
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:900px)");
  // /home（玄関）は夜の参道背景なのでヘッダー・ナビを暗色に馴染ませる
  // 夜の参道トーンのページ（玄関と、始まりの儀式/ログイン）
  const onDarkHome = ["/", "/home", "/top", "/start", "/login"].includes(location.pathname);
  // 玄関（/ と /home）は本文に扉「無料で始める」があるので、ヘッダーには重ねない。
  // 代わりに「ログイン」をスマホでも出す（戻ってきた人の入口が玄関に無かったため）
  const onGenkan = ["/", "/home"].includes(location.pathname);
  // 下のナビ（スマホ）は「/」では出さない。/home から先で出す（2026-11-03 宿主）
  const showBottomNav = isMobile && location.pathname !== "/";

  // 夜の参道トーンのページでは、CssBaseline が body に塗る不透明な明色背景＋
  // グラデを外し、index.html が敷く墨(html #12100d)と玄関背景層(zIndex:-1)を
  // 見せる。これを外さないと body の明色が手前を覆って「まっしろ」になる。
  // 明色ページへ戻るときに元へ復元する。
  useEffect(() => {
    if (!onDarkHome) return undefined;
    const body = document.body;
    const prev = { bg: body.style.backgroundColor, img: body.style.backgroundImage };
    body.style.backgroundColor = "transparent";
    body.style.backgroundImage = "none";
    return () => {
      body.style.backgroundColor = prev.bg;
      body.style.backgroundImage = prev.img;
    };
  }, [onDarkHome]);

  // Mood（静/和/動）
  const { mood, suggested, override, setOverride } = useContext(MoodContext);
  // 今日の気分（2026-10-13 一旦停止。DAILY_MOOD_ENABLED を true に戻せば復活する）
  const { mood: dailyMood, needsSelection, setTodayMood } = useDailyMood();
  const { summary: mileSummary } = useMileBalance();
  const mileBalance = mileSummary?.balance ?? 0;
  const [showMoodScreen, setShowMoodScreen] = useState(false);

  // 初回（今日まだ選んでいない）→ フルスクリーン表示（縁を結んだ人だけ）
  useEffect(() => {
    if (DAILY_MOOD_ENABLED && needsSelection && authUser) setShowMoodScreen(true);
  }, [needsSelection, authUser]);

  // PC用ナビ（全項目）
  const nav = useMemo(() => {
    const items = [
      { label: "近隣の神社", to: "/nearby" },
      { label: "社", to: "/keidai" },
      { label: "参拝ログ", to: "/me/history" },
      { label: "アストロマップ", to: "/astro" },
      { label: "ブログ", to: "/me/blog" },
    ];
    if (isAdmin) items.push({ label: "管理", to: "/admin" });
    return items;
  }, [isAdmin]);

  // Drawer用：BottomNavに入らない項目
  const drawerNav = useMemo(() => {
    const items = [
      { label: "アストロマップ", to: "/astro" },
      { label: "ブログ", to: "/me/blog" },
      { label: "神社を登録", to: "/shrines/register" },
    ];
    if (isAdmin) items.push({ label: "管理", to: "/admin" });
    return items;
  }, [isAdmin]);

  // ページ遷移フェード（全画面オーバーレイ）
  const [overlayOpacity, setOverlayOpacity] = useState(0);
  const [overlayTransition, setOverlayTransition] = useState("none");

  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      // 暗転してからナビゲーション実行
      setOverlayTransition("opacity 0.4s ease");
      setOverlayOpacity(0.88);
      const t = setTimeout(() => blocker.proceed(), 450);
      return () => clearTimeout(t);
    }
  }, [blocker.state]);

  useEffect(() => {
    // コンテンツ切り替わり後に明転
    setOverlayTransition("opacity 1.0s ease");
    setOverlayOpacity(0);
  }, [location.pathname]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountDrawerOpen, setAccountDrawerOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const prevAuthUser = useRef(authUser);

  useEffect(() => {
    if (!prevAuthUser.current && authUser && userDoc && !userDoc.welcomeShown) {
      setSnackMessage("ログインしました");
      updateDoc(doc(db, "users", authUser.uid), { welcomeShown: true }).catch(() => {});
      patchUserDoc({ welcomeShown: true });
    }
    prevAuthUser.current = authUser;
  }, [authUser, userDoc]);

  // ページ遷移でDrawerを閉じる
  useEffect(() => {
    setDrawerOpen(false);
    setAccountDrawerOpen(false);
  }, [location.pathname]);

  const isActive = (to) => {
    if (to === "/") return location.pathname === "/";
    // 「ホーム」は玄関（/, /home）とログイン後のトップ（/top）のどれでも点灯
    if (to === "/home") return ["/", "/home", "/top"].includes(location.pathname);
    return location.pathname.startsWith(to);
  };

  // BottomNav：未ログイン時は「ホーム」だけ（社・ログは縁を結んでから）。
  // 「巡る」も 2026-11-02 ひとまず未ログインには出さない（戻すときは "/nearby" を足す）
  const visibleBottomNavItems = useMemo(
    () => (authUser ? bottomNavItems : bottomNavItems.filter((item) => item.to === "/home")),
    [authUser]
  );

  // BottomNav の現在値
  const bottomNavValue = useMemo(() => {
    const idx = visibleBottomNavItems.findIndex((item) => isActive(item.to));
    return idx >= 0 ? idx : false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, visibleBottomNavItems]);

  /* ── アクティブインジケーター付きナビボタン（PC） ── */
  const NavButton = ({ to, label }) => {
    const active = isActive(to);
    return (
      <Button
        component={RouterLink}
        to={to}
        size="small"
        variant="text"
        sx={{
          position: "relative",
          fontWeight: active ? 900 : 600,
          opacity: active ? 1 : 0.65,
          color: onDarkHome ? "#ece5d4" : "text.primary",
          textTransform: "none",
          px: 1.5,
          "&:hover": { opacity: 1 },
          transition: "opacity 0.2s, font-weight 0.2s",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 4,
            left: "50%",
            transform: "translateX(-50%)",
            width: active ? "60%" : 0,
            height: 2,
            borderRadius: 1,
            bgcolor: onDarkHome ? "#d4b46e" : "primary.main",
            transition: "width 0.25s ease",
          },
          "&:hover::after": {
            width: "40%",
          },
        }}
      >
        {label}
      </Button>
    );
  };

  /* ── CTA（未ログイン時：入口はこの2つだけ。自分自身へのリンクは隠す） ── */
  const cta = ENTRY_BUTTONS_ENABLED && !authUser ? (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {location.pathname !== "/login" && (
      <Button
        component={RouterLink}
        to="/login"
        variant="text"
        size="small"
        sx={{
          fontWeight: 600,
          display: { xs: onGenkan ? "inline-flex" : "none", sm: "inline-flex" },
          color: onDarkHome ? "rgba(222,215,198,0.6)" : "text.secondary",
          ...(onDarkHome && {
            fontFamily: '"Hiragino Mincho ProN", "Yu Mincho", serif',
            letterSpacing: 2,
            fontSize: "0.8rem",
          }),
          "&:hover": { color: onDarkHome ? "#ece5d4" : "text.primary" },
        }}
      >
        ログイン
      </Button>
      )}
      {location.pathname !== "/start" && !onGenkan && (
      <Button
        component={RouterLink}
        to="/start"
        variant="contained"
        size="small"
        sx={{
          fontWeight: 900,
          whiteSpace: "nowrap",
          ...(onDarkHome && {
            fontFamily: '"Hiragino Mincho ProN", "Yu Mincho", serif',
            fontWeight: 600,
            letterSpacing: 2,
            color: "#e6d3a3",
            background: "rgba(212,180,110,0.06)",
            border: "1px solid rgba(212,180,110,0.5)",
            borderRadius: 0,
            boxShadow: "none",
            "&:hover": {
              background: "rgba(212,180,110,0.14)",
              borderColor: "rgba(224,201,143,0.9)",
              boxShadow: "none",
            },
          }),
        }}
      >
        無料で始める
      </Button>
      )}
    </Stack>
  ) : null;

  /* ── モバイルDrawer（「その他」から開く、下からスライド） ── */
  const mobileDrawer = (
    <Drawer
      anchor="bottom"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          bgcolor: "background.default",
          maxHeight: "70dvh",
          pb: `${BOTTOM_NAV_HEIGHT}px`,
        },
      }}
    >
      {/* ハンドル */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 1 }}>
        <Box
          sx={{
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: "text.disabled",
            opacity: 0.4,
          }}
        />
      </Box>

      {/* ナビリンク */}
      <DrawerCardGrid>
        {drawerNav.map((item) => (
          <DrawerCard key={item.to} to={item.to} label={item.label} isActive={isActive} />
        ))}
      </DrawerCardGrid>

      <Divider sx={{ my: 1 }} />

      {/* アカウント */}
      <DrawerCardGrid>
        {authUser ? (
          <>
            <DrawerCard to="/me" label="マイページ" isActive={isActive} />
            {isAdmin && <DrawerCard to="/admin" label="管理者ページ" isActive={isActive} />}
            <DrawerCard
              defKey="logout"
              label="ログアウト"
              onClick={async () => {
                setDrawerOpen(false);
                await signOut();
                setSnackMessage("ログアウトしました");
              }}
            />
          </>
        ) : ENTRY_BUTTONS_ENABLED ? (
          <>
            <DrawerCard to="/start" label="無料で始める" isActive={isActive} />
          </>
        ) : null}
      </DrawerCardGrid>
    </Drawer>
  );

  return (
    <Box sx={{ minHeight: "100dvh", display: "flex", flexDirection: "column" }}>
      {/* 今日の気分フルスクリーン */}
      {DAILY_MOOD_ENABLED && showMoodScreen && (
        <DailyMoodScreen
          onClose={() => setShowMoodScreen(false)}
          onComplete={() => { setShowMoodScreen(false); navigate("/"); }}
        />
      )}

      {/* 全画面暗転オーバーレイ */}
      <Box sx={{ position: "fixed", inset: 0, zIndex: 9999, bgcolor: "#1a1a18", opacity: overlayOpacity, transition: overlayTransition, pointerEvents: "none" }} />
      {/* ── ヘッダー ── */}
      <AppBar
        position="sticky"
        color="transparent"
        elevation={0}
        sx={{
          backdropFilter: "saturate(180%) blur(10px)",
          WebkitBackdropFilter: "saturate(180%) blur(10px)",
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 52, sm: 64 },
            px: { xs: 1.5, sm: 2 },
          }}
        >
          {/* ロゴ */}
          <Typography
            component={RouterLink}
            to="/"
            variant="h6"
            sx={{
              textDecoration: "none",
              color: onDarkHome ? "#ece5d4" : "text.primary",
              fontWeight: 900,
              letterSpacing: -0.5,
              mr: 3,
              whiteSpace: "nowrap",
              fontSize: "1.1rem",
            }}
          >
            古道
          </Typography>

          {/* PC：ナビ（扉は縁を結んでから現れる） */}
          {!isMobile && authUser && (
            <Stack direction="row" spacing={0.25}>
              <NavButton to="/home" label="ホーム" />
              {nav.map((item) => (
                <NavButton key={item.to} to={item.to} label={item.label} />
              ))}
            </Stack>
          )}

          <Box sx={{ flex: 1, minWidth: 0 }} />

          {/* PC：右側コントロール */}
          {!isMobile && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ flexShrink: 0 }}
            >
              {/* 今日の気分インジケーター */}
              {DAILY_MOOD_ENABLED && authUser && dailyMood && (
                <Box
                  onClick={() => setShowMoodScreen(true)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 2,
                    cursor: "pointer",
                    background: MOOD_GRADIENTS[dailyMood.id],
                    boxShadow: "0 1px 6px rgba(0,0,0,0.12)",
                    transition: "opacity 0.2s",
                    "&:hover": { opacity: 0.85 },
                  }}
                >
                  <Typography sx={{ fontSize: "1.1rem", fontFamily: '"Hiragino Mincho ProN","Yu Mincho",serif', color: dailyMood.id === "oso" ? "#e8e8f0" : "rgba(0,0,0,0.75)", lineHeight: 1 }}>
                    {dailyMood.kanji}
                  </Typography>
                  <Typography sx={{ fontSize: "0.6rem", letterSpacing: 2, color: dailyMood.id === "oso" ? "rgba(232,232,240,0.8)" : "rgba(0,0,0,0.5)" }}>
                    {dailyMood.reading}
                  </Typography>
                </Box>
              )}
              {DAILY_MOOD_ENABLED && authUser && !dailyMood && (
                <Button size="small" variant="outlined" onClick={() => setShowMoodScreen(true)}
                  sx={{
                    fontSize: "0.75rem",
                    color: onDarkHome ? "rgba(222,215,198,0.7)" : "text.secondary",
                    borderColor: onDarkHome ? "rgba(212,180,110,0.35)" : "divider",
                  }}>
                  今日の気分
                </Button>
              )}

              {cta}

              {authUser && (
                <Box
                  component={RouterLink}
                  to="/me/miles"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 2,
                    textDecoration: "none",
                    bgcolor: onDarkHome ? "rgba(212,180,110,0.12)" : "rgba(180,83,9,0.08)",
                    "&:hover": { bgcolor: onDarkHome ? "rgba(212,180,110,0.2)" : "rgba(180,83,9,0.15)" },
                    transition: "background-color 0.2s",
                  }}
                >
                  <LocalActivityRoundedIcon sx={{ color: onDarkHome ? "#d4b46e" : "#b45309", fontSize: 15 }} />
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, color: onDarkHome ? "#d4b46e" : "#b45309" }}>
                    {mileBalance.toLocaleString()}
                  </Typography>
                </Box>
              )}

              {authUser && (
                <IconButton
                  onClick={() => setAccountDrawerOpen(true)}
                  aria-label="account"
                  sx={{ color: onDarkHome ? "rgba(222,215,198,0.7)" : "text.secondary" }}
                >
                  <AccountCircleRoundedIcon />
                </IconButton>
              )}
            </Stack>
          )}

          {/* モバイル：気分チップ + CTA + アカウントアイコン */}
          {isMobile && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {DAILY_MOOD_ENABLED && authUser && (dailyMood ? (
                <Box
                  onClick={() => setShowMoodScreen(true)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    cursor: "pointer",
                    background: MOOD_GRADIENTS[dailyMood.id],
                    boxShadow: "0 1px 6px rgba(0,0,0,0.15)",
                    flexShrink: 0,
                  }}
                >
                  <Typography sx={{ fontSize: "1rem", fontFamily: '"Hiragino Mincho ProN","Yu Mincho",serif', color: dailyMood.id === "oso" ? "#e8e8f0" : "rgba(0,0,0,0.75)", lineHeight: 1 }}>
                    {dailyMood.kanji}
                  </Typography>
                </Box>
              ) : (
                <IconButton onClick={() => setShowMoodScreen(true)} size="small" sx={{ color: onDarkHome ? "rgba(222,215,198,0.7)" : "text.secondary", fontSize: "0.7rem", fontWeight: 700 }}>
                  気分
                </IconButton>
              ))}
              {cta}
              {authUser && (
                <Box
                  component={RouterLink}
                  to="/me/miles"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.4,
                    px: 1,
                    py: 0.4,
                    borderRadius: 2,
                    textDecoration: "none",
                    bgcolor: onDarkHome ? "rgba(212,180,110,0.12)" : "rgba(180,83,9,0.08)",
                  }}
                >
                  <LocalActivityRoundedIcon sx={{ color: onDarkHome ? "#d4b46e" : "#b45309", fontSize: 14 }} />
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: onDarkHome ? "#d4b46e" : "#b45309" }}>
                    {mileBalance.toLocaleString()}
                  </Typography>
                </Box>
              )}
              {authUser && (
                <IconButton
                  onClick={() => setAccountDrawerOpen(true)}
                  aria-label="account"
                  sx={{ color: onDarkHome ? "rgba(222,215,198,0.7)" : "text.secondary" }}
                >
                  <AccountCircleRoundedIcon />
                </IconButton>
              )}
            </Stack>
          )}
        </Toolbar>
      </AppBar>

      {/* ── 右ドロワー（アカウントアイコンから開く） ── */}
      <Drawer
        anchor="right"
        open={accountDrawerOpen}
        onClose={() => setAccountDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "88vw", sm: 420 },
            bgcolor: "background.default",
            pt: 1,
          },
        }}
      >
        {/* 閉じるボタン */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, pt: 0.5 }}>
          <IconButton onClick={() => setAccountDrawerOpen(false)} aria-label="close">
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        {/* ナビリンク */}
        <DrawerCardGrid>
          {[{ label: "ホーム", to: "/home" }, ...nav].map((item) => (
            <DrawerCard key={item.to} to={item.to} label={item.label} isActive={isActive} />
          ))}
        </DrawerCardGrid>

        <Divider sx={{ my: 1 }} />

        {/* アカウント */}
        <DrawerCardGrid>
          {authUser ? (
            <>
              <DrawerCard to="/me" label="マイページ" isActive={isActive} />
              {isAdmin && <DrawerCard to="/admin" label="管理者ページ" isActive={isActive} />}
              <DrawerCard
                defKey="logout"
                label="ログアウト"
                onClick={async () => {
                  setAccountDrawerOpen(false);
                  await signOut();
                  setSnackMessage("ログアウトしました");
                }}
              />
            </>
          ) : ENTRY_BUTTONS_ENABLED ? (
            <>
              <DrawerCard to="/start" label="無料で始める" isActive={isActive} />
            </>
          ) : null}
        </DrawerCardGrid>
      </Drawer>

      {mobileDrawer}

      {/* ── メインコンテンツ ── */}
      <Box
        component="main"
        sx={{
          flex: 1,
          pb: showBottomNav ? `${BOTTOM_NAV_HEIGHT + 16}px` : isMobile ? 2 : 6,
        }}
      >
        <Container sx={{ pt: 2 }}>
          {/* 遅延ページの読み込み中はシェルを保ったまま静かに待つ */}
          <Suspense
            fallback={
              <Box
                sx={{
                  py: 12,
                  textAlign: "center",
                  color: onDarkHome ? "rgba(222,215,198,0.4)" : "text.disabled",
                  fontSize: 22,
                  animation: "kodoPageWait 1.6s ease-in-out infinite",
                  "@keyframes kodoPageWait": {
                    "0%, 100%": { opacity: 0.35 },
                    "50%": { opacity: 0.9 },
                  },
                }}
              >
                ⛩
              </Box>
            }
          >
            <Outlet />
          </Suspense>
        </Container>
      </Box>

      {/* PC：フッター */}
      {!isMobile && <Footer dark={onDarkHome} guest={!authUser} />}

      {/* ── モバイル：BottomNavigation（「/」では出さない） ── */}
      {showBottomNav && (
        <Paper
          elevation={0}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar,
            borderRadius: 0,
            borderTop: `1px solid ${onDarkHome ? "rgba(212,180,110,0.2)" : theme.palette.divider}`,
            backdropFilter: "saturate(180%) blur(12px)",
            WebkitBackdropFilter: "saturate(180%) blur(12px)",
            bgcolor: onDarkHome ? "rgba(18,16,13,0.85)" : "rgba(255,255,255,0.85)",
          }}
        >
          <BottomNavigation
            value={bottomNavValue}
            onChange={(_, newValue) => {
              if (visibleBottomNavItems[newValue]?.to) navigate(visibleBottomNavItems[newValue].to);
            }}
            showLabels
            sx={{
              height: BOTTOM_NAV_HEIGHT,
              bgcolor: "transparent",
              "& .MuiBottomNavigationAction-root": {
                minWidth: 0,
                py: 1,
                color: onDarkHome ? "rgba(222,215,198,0.6)" : "text.secondary",
                opacity: 0.55,
                transition: "opacity 0.2s",
                "&.Mui-selected": {
                  opacity: 1,
                  color: onDarkHome ? "#d4b46e" : "primary.main",
                },
              },
              "& .MuiBottomNavigationAction-label": {
                fontSize: "0.65rem",
                fontWeight: 700,
                mt: 0.25,
                "&.Mui-selected": {
                  fontSize: "0.65rem",
                  fontWeight: 900,
                },
              },
            }}
          >
            {visibleBottomNavItems.map((item) => (
              <BottomNavigationAction
                key={item.to}
                label={item.label}
                icon={item.icon}
              />
            ))}
            {authUser && (
              <BottomNavigationAction
                label="その他"
                icon={<MoreHorizRoundedIcon />}
                onClick={(e) => {
                  e.preventDefault();
                  setDrawerOpen(true);
                }}
              />
            )}
          </BottomNavigation>
        </Paper>
      )}

      <Snackbar
        open={!!snackMessage}
        autoHideDuration={3000}
        onClose={() => setSnackMessage("")}
        message={snackMessage}
        anchorOrigin={{
          vertical: isMobile ? "top" : "bottom",
          horizontal: "center",
        }}
      />

      {/* ── AIアドバイザー みこちゃん ── */}
    </Box>
  );
}
